// Portada: todos los juegos a la vista, como iconos de app
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { useAuthStore } from '../store/authStore';
import { GAMES, type GameInfo } from '../games/catalog';
import GameIcon from '../components/GameIcon';
import { API_URL } from '../lib/api';


const formatCount = (n: number) => n.toLocaleString('es-ES');

function GameTile({ game, plays, href, index }: { game: GameInfo; plays?: number; href: string; index: number }) {
  return (
    <motion.li
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', bounce: 0, duration: 0.5, delay: 0.04 * index }}
    >
      <Link
        to={href}
        className="group flex flex-col items-center text-center gap-3 rounded-3xl px-2 pt-5 pb-4 outline-none transition-colors hover:bg-ink/[0.035] focus-visible:bg-ink/[0.05]"
      >
        <span className="transition-transform duration-150 ease-out group-active:scale-[0.94] group-hover:-translate-y-0.5">
          <GameIcon id={game.id} size={96} shared />
        </span>
        <span className="flex flex-col gap-1 min-w-0">
          <span className="text-title3 font-semibold text-ink">{game.name}</span>
          <span className="text-footnote text-ink-2 max-w-[15rem]">{game.tagline}</span>
          {plays !== undefined && plays > 0 && (
            <span className="text-caption text-ink-3 tabular mt-0.5">
              {formatCount(plays)} {plays === 1 ? 'partida' : 'partidas'}
            </span>
          )}
        </span>
      </Link>
    </motion.li>
  );
}

export default function Home() {
  const { isAuthenticated, user } = useAuthStore();
  const [plays, setPlays] = useState<Record<string, number>>({});
  const totalPlays = Object.values(plays).reduce((sum, n) => sum + n, 0);

  useEffect(() => {
    fetch(`${API_URL}/api/stats/plays`)
      .then((r) => r.json())
      .then((json) => { if (json.success) setPlays(json.data); })
      .catch(() => {});
  }, []);

  const hrefFor = (game: GameInfo) =>
    game.needsAccount && !isAuthenticated ? '/auth' : game.route;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <section className="pt-14 sm:pt-20 pb-6 sm:pb-10">
        <motion.h1
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', bounce: 0, duration: 0.6 }}
          className="text-display font-bold text-ink text-balance"
        >
          {user ? <>Hola, {user.username}.<br /><span className="text-ink-3">Elige un juego.</span></> : 'Elige un juego.'}
        </motion.h1>
        {!isAuthenticated && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.16 }}
            className="mt-7 flex flex-wrap items-center gap-3"
          >
            <Link to="/auth?tab=guest" className="btn-primary btn-lg">Jugar como invitado</Link>
            <Link to="/auth?tab=register" className="btn-plain btn-lg">
              Crear cuenta <ChevronRightIcon className="w-5 h-5 -ml-1" />
            </Link>
          </motion.div>
        )}
      </section>

      <section aria-labelledby="juegos" className="pb-16">
        <h2 id="juegos" className="sr-only">Juegos</h2>
        <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-2 gap-y-4 -mx-2">
          {GAMES.map((game, i) => (
            <GameTile key={game.id} game={game} plays={plays[game.id]} href={hrefFor(game)} index={i} />
          ))}
        </ul>
      </section>

      <section className="pb-20">
        <Link
          to="/ranking"
          className="group panel flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8 p-6 sm:p-8 transition-colors hover:bg-raised"
        >
          <div className="flex-1 min-w-0">
            <h2 className="text-title2 font-semibold text-ink">Compite sin esperar a nadie.</h2>
            <p className="mt-2 text-body text-ink-2 max-w-2xl text-pretty">
              Cada juego tiene su ranking: el mismo nivel para todos y gana la mejor marca.
              {!user || user.isGuest ? ' Crea una cuenta para aparecer en la clasificación.' : ''}
            </p>
          </div>
          {/* Contador público de partidas: prueba de actividad junto a la invitación a competir */}
          {totalPlays > 0 && (
            <div className="shrink-0 flex sm:flex-col items-baseline sm:items-end gap-2 sm:gap-0 sm:pr-8 sm:border-r border-line/60">
              <span className="text-title1 font-bold text-ink tabular">{formatCount(totalPlays)}</span>
              <span className="text-footnote text-ink-2">{totalPlays === 1 ? 'partida jugada' : 'partidas jugadas'}</span>
            </div>
          )}
          <span className="btn-secondary shrink-0 self-start sm:self-auto group-hover:bg-ink/10">
            Ver ranking
          </span>
        </Link>
      </section>
    </div>
  );
}
